import { GetStaticProps, NextPage } from 'next';
import { SSRConfig, useTranslation } from 'next-i18next';
import i18nextConfig from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import tw, { styled } from 'twin.macro';
import { googleFormUrl } from 'core/view/Gnb';
import { Item1, Item2, Item3, Item4 } from 'core/view/association';
import * as Icon1 from 'core/view/association1';

const InnerBox = styled.div`
  ${tw`max-w-[1024px] mx-auto px-[24px] md:px-[80px] box-content whitespace-pre-line`}
`;

const ItemBox = styled.article`
  ${tw`text-center [& svg]:mx-auto`};

  --item-bg-color: transparent;
  --point-color: #eeeeee;

  & > div {
    ${tw`w-[fit-content] mx-auto`}
  }

  & > div:hover {
    @media (min-width: 768px) {
      --item-bg-color: #e2ebf2;
      --point-color: #6d9bbd;

      path {
        ${tw`transition-all duration-1000`}; // 0.3s
      }
    }
  }

  @media (max-width: 767px) {
    --point-color: #6d9bbd;
  }

  svg {
    ${tw`h-[140px] w-[156px] md:h-[200px] md:w-[240px]`}
  }

  header {
    ${tw`text-[18px] md:text-[20px] font-weight[800] line-height[1.8em] text-[#111]`};
    ${tw`font-NanumSquare`}
  }
  p {
    ${tw`text-[12px] md:text-[14px] line-height[2em] text-[#666]`};
  }

  p span {
    ${tw`inline-block mx-[2px]`}
  }
`;

const ItemBox1 = styled.article`
  ${tw`text-center [& svg]:mx-auto`};

  & > div {
    ${tw`w-[fit-content] mx-auto`}
  }

  svg {
    ${tw`mb-[20px]`}
  }
  header {
    ${tw`text-[18px] md:text-[22px] font-weight[800] text-white line-height[1.8em]`}
    ${tw`font-NanumSquare`}
  }
  p {
    ${tw`text-[12px] md:text-[16px] text-[#ddd] line-height[2em]`}
  }
  p span {
    ${tw`inline-block mx-[2px]`}
  }

  & > div {
    ${tw`opacity-80`}
    ${tw`transition-all duration-300`}; // 0.3s
  }

  & > div:hover {
    ${tw`opacity-100`}
  }

  @media (max-width: 767px) {
    & > div {
      ${tw`opacity-100`}
    }
  }
`;

const Association: NextPage = () => {
  const { t, i18n } = useTranslation('association');
  const lang = i18n.resolvedLanguage;

  return (
    <>
      <InnerBox>
        <div css={[tw`h-[80px] md:h-[100px]`]} />
        <header
          css={[tw`flex flex-col text-center gap-[12px]`, tw`line-height[1]`]}
        >
          {t('header.subInfo') && (
            <span
              css={[
                tw`text-[18px] text-[#333] font-weight[800] font-NanumSquare`,
              ]}
            >
              {t('header.subInfo')}
            </span>
          )}
          <span
            css={[
              tw`text-[38px] text-[#111] font-weight[700] font-OpenSans`,
              tw`text-transform[uppercase]`,
            ]}
          >
            Association
          </span>
        </header>

        <div css={[tw`h-[60px] md:h-[100px]`]} />
        <div
          css={[
            tw`mx-auto text-center`,
            // tw`pt-[60px] pb-[60px] md:pt-[100px] md:pb-[60px]`,
          ]}
        >
          <div css={[tw`inline-block`]}>
            <img
              src={`${process.env.NEXT_PUBLIC_CLOUD_FRONT}/partners/ssak/association/icon.jpg`}
              srcSet={`${process.env.NEXT_PUBLIC_CLOUD_FRONT}/partners/ssak/association/icon.jpg 1x, ${process.env.NEXT_PUBLIC_CLOUD_FRONT}/partners/ssak/association/icon@2x.jpg 2x`}
              alt=""
              width={240}
              height={200}
              loading="lazy"
            />
            {/* <Icon /> */}
          </div>
        </div>

        <div css={[tw`h-[60px] md:h-[100px]`]} />

        <article
          css={[
            tw`flex flex-col gap-[50px] break1280:grid break1280:grid-cols-[auto 1fr] break1280:gap-20`,
          ]}
        >
          <header
            css={[
              tw`text-[24px] md:text-[28px] break1280:text-[34px] font-weight[800] font-NanumSquare`,
              tw`break1280:w-[682px]`,
              tw`line-height[2em]`,
            ]}
          >
            {t('section1.info')}
          </header>
          <div css={[tw`font-NotoSansCJKkr`, tw`line-height[2em]`]}>
            <p
              css={[
                tw`text-[18px] break1280:text-[20px] font-medium text-[#111]`,
              ]}
            >
              {t('section1.desc1')}
            </p>
            <p css={[tw`text-[16px] text-[#333]`, tw`mt-[10px]`]}>
              {t('section1.desc2')}
            </p>
          </div>
        </article>
        <div css={[tw`h-[120px] md:h-[160px]`]} />
      </InnerBox>

      <div css={[tw`bg-[#F9F9F9]`]}>
        <InnerBox css={tw`text-center`}>
          <div css={[tw`h-[80px] md:h-[100px]`]} />
          <span
            css={[
              tw`text-[#A7C3D7] text-[14px] line-height[2em] font-weight[700]`,
              tw`bg-[#111111] p-[2px 12px] rounded-full`,
              tw`mb-[10px] inline-block`,
            ]}
          >
            Mission
          </span>

          <header
            css={[
              tw` font-weight[800] line-height[1.6em] text-[#111]`,
              tw`text-[26px] md:text-[34px] break1280:text-[38px]`,
              tw`font-NanumSquare`,
            ]}
          >
            {t('section2.h1')}
          </header>
          <p
            css={[
              tw`text-[16px] font-weight[500] text-[#333] line-height[2em]`,
            ]}
          >
            {t('section2.desc')}
          </p>

          <div css={[tw`h-[80px] md:h-[100px]`]} />

          <article
            css={[
              tw`grid grid-cols-[1fr 1fr] break1280:grid-cols-[1fr 1fr 1fr 1fr] gap-y-[60px] md:gap-y-[100px]`,
            ]}
          >
            <ItemBox>
              <div>
                <Item1 />
                <header>{t('section2.mission.eco.title')}</header>
                <p>
                  <span>{t('section2.mission.eco.desc')}</span>
                </p>
              </div>
            </ItemBox>
            <ItemBox>
              <div>
                <Item2 />
                <header>{t('section2.mission.coSupport.title')}</header>
                <p>
                  <span>{t('section2.mission.coSupport.desc')}</span>
                </p>
              </div>
            </ItemBox>
            <ItemBox>
              <div>
                <Item3 />
                <header>{t('section2.mission.policy.title')}</header>
                <p>{t('section2.mission.policy.desc')}</p>
              </div>
            </ItemBox>
            <ItemBox>
              <div>
                <Item4 />
                <header>{t('section2.mission.globalNetwork.title')}</header>
                <p>
                  <span>{t('section2.mission.globalNetwork.desc')}</span>
                </p>
              </div>
            </ItemBox>
          </article>
          <div css={[tw`h-[120px] md:h-[160px]`]} />
        </InnerBox>
      </div>

      <div
        css={[tw`relative`]}
        style={{
          backgroundImage: `url(${process.env.NEXT_PUBLIC_CLOUD_FRONT}/partners/ssak/association/bg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <InnerBox css={tw`text-center`}>
          <div css={[tw`h-[80px] md:h-[100px]`]} />
          <span
            css={[
              tw`text-[#111111] text-[14px] line-height[2em] font-weight[700]`,
              tw`bg-[#A7C3D7] p-[2px 12px] rounded-full`,
              tw`mb-[10px] inline-block`,
            ]}
          >
            Activity
          </span>

          <header
            css={[
              tw` font-weight[800] line-height[1.6em] text-[#fff]`,
              tw`text-[26px] md:text-[34px] break1280:text-[38px]`,
              tw`font-NanumSquare`,
            ]}
          >
            {t('section3.h1')}
          </header>
          <div css={[tw`h-[80px] md:h-[100px]`]} />

          <div
            css={[
              tw`grid grid-cols-[1fr 1fr] break1280:grid-cols-[1fr 1fr 1fr] gap-y-[60px] md:gap-y-[80px] gap-x-[4px]`,
            ]}
          >
            <ItemBox1>
              <div>
                <Icon1.Item1 />
                <header>{t('section3.activity.growthPrograms.title')}</header>
                <p>
                  <span>{t('section3.activity.growthPrograms.desc')}</span>
                </p>
              </div>
            </ItemBox1>

            <ItemBox1>
              <div>
                <Icon1.Item2 />
                <header>{t('section3.activity.enablingBusiness.title')}</header>
                <p>{t('section3.activity.enablingBusiness.desc')}</p>
              </div>
            </ItemBox1>

            <ItemBox1 css={[tw`col-span-2 break1280:col-span-1`]}>
              <div>
                <Icon1.Item3 />
                <header>{t('section3.activity.community.title')}</header>
                <p>{t('section3.activity.community.desc')}</p>
              </div>
            </ItemBox1>
          </div>

          <div
            css={[
              tw`grid grid-cols-[1fr 1fr] gap-x-[4px]`,
              tw`mt-[60px] md:mt-[80px]`,
              tw`break1280:w-[fit-content] break1280:mx-auto break1280:[& article]:w-[340px]`,
            ]}
          >
            <ItemBox1>
              <div>
                <Icon1.Item4 />
                <header>{t('section3.activity.policy.title')}</header>
                <p>{t('section3.activity.policy.desc')}</p>
              </div>
            </ItemBox1>

            <ItemBox1>
              <div>
                <Icon1.Item5 />
                <header>{t('section3.activity.global.title')}</header>
                <p>{t('section3.activity.global.desc')}</p>
              </div>
            </ItemBox1>
          </div>

          <a
            href={googleFormUrl(lang)}
            rel="noreferrer"
            target="_blank"
            css={[
              tw`inline-block`,
              tw`px-24 py-8`,
              tw`mt-100 x_430:(mt-60)`,
              tw`border[1px solid #fff] rounded-full`,
              tw`text-16 leading-24 font-extrabold text-[#fff] font-NanumSquare`,
            ]}
          >
            {t('section3.join')}
          </a>
        </InnerBox>

        <div css={[tw`h-[120px] md:h-[160px]`]} />
      </div>
    </>
  );
};

export default Association;

export const getStaticProps: GetStaticProps<SSRConfig> = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(
        locale as string,
        ['home', 'association'],
        i18nextConfig,
      )),
    },
  };
};
